import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import { parse, stringify } from "query-string";
import AuthorizedApolloProvider from "@src/contexts/AuthorizedApolloProvider";
import LoginWithToken from "@src/pages/LoginWithToken";
import { PrivateRoute } from "@src/components/elements/PrivateRoute";
import { AuthProvider } from "@src/contexts/AuthProvider";
import { NotFound } from "@src/pages/NotFound";
import { SuperPrivateRoute } from "@src/components/elements/SuperPrivateRoute";
import { Page } from "./shared/pages";
import { SessionProvider } from "@src/contexts/SessionContext";
import { ErrorBoundary } from "@src/ErrorBoundary";
import { HeadcountGatekeeper } from "@src/pages/headcount-tracking/HeadcountGatekeeper";
import { PaymentsGatekeeper } from "@src/pages/payments/PaymentsGatekeeper";
import { ImpersonationBorder } from "@src/shared/ImpersonationBorder";
import { MaybeSwitchCompany } from "@src/contexts/MaybeSwitchCompany";
import { PrivateVendorRoute } from "@src/components/elements/PrivateVendorRoute";
import { GoogleMapsProvider } from "@src/contexts/GoogleMapsProvider";
import { NavProvider } from "@src/contexts/NavContext";
import { QueryClientProvider } from "react-query";
import "@src/styles.css";
import { queryClient } from "@src/reactQueryClient";
import { PageLoading } from "@src/components/elements/assets/PageLoading";

export type QueryParams = {
  companyId: string;
  vendorId: string;
  serviceId: string;
  memberId: string;
  documentId: string;
  spendRequestId: string;
  groupId: string;
  departmentId: string;
  planId: string;
  previewDocumentId: string;
  billId: string;
  billPaymentId: string;
  ruleSetId: string;
};

function reloadPage(error: Error) {
  console.log({ error });
  if (error.message.startsWith("Failed to fetch dynamically imported module")) {
    window.location.reload();
    // eslint-disable-next-line react/display-name, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    return { default: (args: any): any => <div></div> };
  }
  throw error;
}

const ManageApiKeys = lazy(() =>
  import("./pages/settings/APIKeys/ManageApiKeys").catch(reloadPage),
);
const SourcingSettings = lazy(() =>
  import("./pages/settings/sourcing/SourcingSettings").catch(reloadPage),
);
const Login = lazy(() => import("./pages/Login").catch(reloadPage));
const Home = lazy(() => import("./pages/home/Home").catch(reloadPage));
const Vendors = lazy(() => import("./pages/vendor/Vendors").catch(reloadPage));
const VendorForms = lazy(() =>
  import("./pages/vendor/VendorForms").catch(reloadPage),
);
const LedgerEntries = lazy(() =>
  import("./pages/payments/ledger-entries/LedgerEntries").catch(reloadPage),
);
const AddNewVendor = lazy(() =>
  import("./pages/vendor/AddNewVendor/AddNewVendor").catch(reloadPage),
);
const EditBill = lazy(() =>
  import("./pages/payments/bills/EditBill").catch(reloadPage),
);
const BillRulesSimulate = lazy(() =>
  import("./pages/settings/approvals/bill-rules/BillRulesSimulate").catch(
    reloadPage,
  ),
);
const EmailForwarding = lazy(() =>
  import("./pages/email-forwarding/EmailForwarding").catch(reloadPage),
);
const VendorDetails = lazy(() =>
  import("./pages/vendor/VendorDetails/VendorDetails").catch(reloadPage),
);
const UpdateVendor = lazy(() =>
  import("./pages/vendor/UpdateVendor/UpdateVendor").catch(reloadPage),
);
const Services = lazy(() =>
  import("./pages/service/Services").catch(reloadPage),
);
const AddNewService = lazy(() =>
  import("./pages/service/AddNewService/AddNewService").catch(reloadPage),
);
const ServiceDetails = lazy(() =>
  import("./pages/service/ServiceDetails/ServiceDetails").catch(reloadPage),
);
const UpdateService = lazy(() =>
  import("./pages/service/UpdateService/UpdateService").catch(reloadPage),
);
const Members = lazy(() => import("./pages/members/Members").catch(reloadPage));
const MemberDetails = lazy(() =>
  import("./pages/members/MemberDetails/MemberDetails").catch(reloadPage),
);
const UpdateMember = lazy(() =>
  import("./pages/members/UpdateMember/UpdateMember").catch(reloadPage),
);
const SpendRequests = lazy(() =>
  import("./pages/spend-request/SpendRequests").catch(reloadPage),
);
const SpendRequestDetails = lazy(() =>
  import("./pages/spend-request/SpendRequestDetails/SpendRequestDetails").catch(
    reloadPage,
  ),
);
const AddNewSpendRequest = lazy(() =>
  import("./pages/spend-request/AddNewSpendRequest/AddNewSpendRequest").catch(
    reloadPage,
  ),
);
const DuplicateSpendRequest = lazy(() =>
  import(
    "./pages/spend-request/AddNewSpendRequest/DuplicateSpendRequest"
  ).catch(reloadPage),
);
const UpdateSpendRequest = lazy(() =>
  import("./pages/spend-request/UpdateSpendRequest/UpdateSpendRequest").catch(
    reloadPage,
  ),
);
const SpendCategories = lazy(() =>
  import("./pages/settings/categories/SpendCategories/SpendCategories").catch(
    reloadPage,
  ),
);
const RevenueCategories = lazy(() =>
  import(
    "./pages/settings/categories/RevenueCategories/RevenueCategories"
  ).catch(reloadPage),
);
const Documents = lazy(() =>
  import("./pages/document/Documents/Documents").catch(reloadPage),
);
const DocumentDetails = lazy(() =>
  import("./pages/document/DocumentDetails/DocumentDetails").catch(reloadPage),
);
const AddNewDocuments = lazy(() =>
  import("./pages/document/AddNewDocuments/AddNewDocuments").catch(reloadPage),
);
const UpdateDocument = lazy(() =>
  import("./pages/document/UpdateDocument/UpdateDocument").catch(reloadPage),
);
const SuperAdmin = lazy(() =>
  import("./pages/SuperAdmin/SuperAdmin").catch(reloadPage),
);
const EditCompany = lazy(() =>
  import("./pages/SuperAdmin/EditCompany").catch(reloadPage),
);
const ManageGroups = lazy(() =>
  import("./pages/settings/ManageGroups/ManageGroups").catch(reloadPage),
);
const ManageApproverMatrix = lazy(() =>
  import("./pages/settings/ManageApproverMatrix/ManageApproverMatrix").catch(
    reloadPage,
  ),
);
const ManageDepartments = lazy(() =>
  import("./pages/settings/ManageDepartments/ManageDepartments").catch(
    reloadPage,
  ),
);
const ManageEntities = lazy(() =>
  import("./pages/settings/ManageEntities/ManageEntities").catch(reloadPage),
);
const Plans = lazy(() =>
  import("./pages/budget-tracking/budgets/Plans").catch(reloadPage),
);
const PlanUpload = lazy(() =>
  import("./pages/budget-tracking/budget-import/PlanUpload").catch(reloadPage),
);
const MapPlanDepartments = lazy(() =>
  import("./pages/budget-tracking/budget-import/MapPlanDepartments").catch(
    reloadPage,
  ),
);
const MapPlanSpendCategories = lazy(() =>
  import(
    "./pages/budget-tracking/budget-import/MapPlanExpenseCategories"
  ).catch(reloadPage),
);
const MapPlanRevenueCategories = lazy(() =>
  import(
    "./pages/budget-tracking/budget-import/MapPlanRevenueCategories"
  ).catch(reloadPage),
);
const MapPlanLocations = lazy(() =>
  import("./pages/budget-tracking/budget-import/MapPlanLocations").catch(
    reloadPage,
  ),
);
const MapPlanVendors = lazy(() =>
  import("./pages/budget-tracking/budget-import/MapPlanVendors").catch(
    reloadPage,
  ),
);
const MapPlanBusinessUnits = lazy(() =>
  import("./pages/budget-tracking/budget-import/MapPlanBusinessUnits").catch(
    reloadPage,
  ),
);

const EditPlan = lazy(() =>
  import("./pages/budget-tracking/budgets/EditPlan").catch(reloadPage),
);
const Actuals = lazy(() =>
  import("./pages/budget-tracking/actuals/Actuals").catch(reloadPage),
);

const Transactions = lazy(() =>
  import("./pages/budget-tracking/actuals/Transactions").catch(reloadPage),
);

const HeadcountImport = lazy(() =>
  import("./pages/headcount-tracking/HeadcountImport").catch(reloadPage),
);

const PlansDashboard = lazy(() =>
  import("./pages/budget-tracking/budgets/PlansDashboard").catch(reloadPage),
);

const HeadcountRoster = lazy(() =>
  import("./pages/headcount-tracking/HeadcountRoster").catch(reloadPage),
);

const HeadcountInsights = lazy(() =>
  import(
    "./pages/headcount-tracking/HeadcountInsights/HeadcountInsights"
  ).catch(reloadPage),
);

const ManageHrDepartments = lazy(() =>
  import(
    "./pages/settings/manage-mappings/manage-hr-mappings/ManageHrDepartments"
  ).catch(reloadPage),
);

const ManageHrLocations = lazy(() =>
  import(
    "./pages/settings/manage-mappings/manage-hr-mappings/ManageHrLocations"
  ).catch(reloadPage),
);

const FinancialInsights = lazy(() =>
  import("./pages/financial-insights/FinancialInsights").catch(reloadPage),
);

const ManageActualsMappings = lazy(() =>
  import("./pages/settings/manage-mappings/ManageActualsMappings").catch(
    reloadPage,
  ),
);
const ManageDepartmentMappings = lazy(() =>
  import("./pages/settings/manage-mappings/ManageDepartmentMappings").catch(
    reloadPage,
  ),
);
const ManagePlanExpenseCategoryMappings = lazy(() =>
  import(
    "./pages/settings/manage-mappings/ManagePlanExpenseCategoryMappings"
  ).catch(reloadPage),
);
const ManagePlanRevenueCategoryMappings = lazy(() =>
  import(
    "./pages/settings/manage-mappings/ManagePlanRevenueCategoryMappings"
  ).catch(reloadPage),
);
const ManageLocationMappings = lazy(() =>
  import("./pages/settings/manage-mappings/ManageLocationMappings").catch(
    reloadPage,
  ),
);
const ManageVendorMappings = lazy(() =>
  import("./pages/settings/manage-mappings/ManageVendorMappings").catch(
    reloadPage,
  ),
);
const ManagePaymentsSettings = lazy(() =>
  import("./pages/settings/Payments/ManagePaymentsSettings").catch(reloadPage),
);
const ManagePaymentsSettingsPaymentInfoApprovers = lazy(() =>
  import(
    "./pages/settings/Payments/ManagePaymentsSettingsPaymentInfoApprovers"
  ).catch(reloadPage),
);
const ManagePlanBusinessUnitMappings = lazy(() =>
  import(
    "./pages/settings/manage-mappings/ManagePlanBusinessUnitMappings"
  ).catch(reloadPage),
);
const Integrations = lazy(() =>
  import("./pages/settings/Integrations/Integrations").catch(reloadPage),
);
const IntegrationsMappings = lazy(() =>
  import("./pages/settings/Integrations/IntegrationsMappings").catch(
    reloadPage,
  ),
);
const NotificationSettings = lazy(() =>
  import("./pages/settings/NotificationSettings/NotificationSettings").catch(
    reloadPage,
  ),
);
const NotiticationSettingsReminders = lazy(() =>
  import(
    "./pages/settings/NotificationSettings/NotificationSettingsReminders"
  ).catch(reloadPage),
);
const NotiticationSettingsVendors = lazy(() =>
  import(
    "./pages/settings/NotificationSettings/NotificationSettingsVendors"
  ).catch(reloadPage),
);
const UserNotificationSettings = lazy(() =>
  import("./pages/settings/AccountSettings/UserNotificationSettings").catch(
    reloadPage,
  ),
);
const DelegateApproverSettings = lazy(() =>
  import("./pages/settings/AccountSettings/DelegateApproverSettings").catch(
    reloadPage,
  ),
);
const Locations = lazy(() =>
  import("./pages/settings/Locations/Locations").catch(reloadPage),
);
const SpendRulesSimulate = lazy(() =>
  import("./pages/settings/approvals/spend-rules/SpendRulesSimulate").catch(
    reloadPage,
  ),
);
const SpendRuleManager = lazy(() =>
  import("./pages/settings/approvals/spend-rules/SpendRuleManager").catch(
    reloadPage,
  ),
);
const BillRuleManager = lazy(() =>
  import("./pages/settings/approvals/bill-rules/BillRuleManager").catch(
    reloadPage,
  ),
);
const ManageBusinessUnits = lazy(() =>
  import("./pages/settings/ManageBusinessUnits/ManageBusinessUnits").catch(
    reloadPage,
  ),
);
const BillPayments = lazy(() =>
  import("./pages/payments/bill-payments/BillPayments").catch(reloadPage),
);
const BillPaymentDetails = lazy(() =>
  import("./pages/payments/bill-payments/BillPaymentDetails").catch(reloadPage),
);
const Invoices = lazy(() =>
  import("./pages/payments/invoices/Invoices").catch(reloadPage),
);
const Bills = lazy(() =>
  import("./pages/payments/bills/Bills").catch(reloadPage),
);
const NewBill = lazy(() =>
  import("./pages/payments/bills/NewBill").catch(reloadPage),
);
const BillDetails = lazy(() =>
  import("./pages/payments/bills/BillDetails").catch(reloadPage),
);
const PaymentsTransactions = lazy(() =>
  import("./pages/payments/PaymentsTransactions").catch(reloadPage),
);
const VendorLogin = lazy(() =>
  import("./pages/vendor-portal/VendorLogin").catch(reloadPage),
);
const VendorPortalHome = lazy(() =>
  import("./pages/vendor-portal/VendorPortalHome/VendorPortalHome").catch(
    reloadPage,
  ),
);
const VendorPortalPayments = lazy(() =>
  import("./pages/vendor-portal/payments/VendorPortalPayments").catch(
    reloadPage,
  ),
);
const VendorPortalCompanyProfile = lazy(() =>
  import(
    "./pages/vendor-portal/CompanyProfile/VendorPortalCompanyProfile"
  ).catch(reloadPage),
);
const VendorPortalMembers = lazy(() =>
  import("./pages/vendor-portal/Members/VendorPortalMembers").catch(reloadPage),
);
const VendorPortalCustomers = lazy(() =>
  import(
    "./pages/vendor-portal/vendor-portal-customers/VendorPortalCustomers"
  ).catch(reloadPage),
);
const ManagePaymentsOnboarding = lazy(() =>
  import("./pages/payments/ManagePaymentsOnboarding").catch(reloadPage),
);
const SyncedLedgerEntries = lazy(() =>
  import("./pages/payments/ledger-entries/LedgerEntries").catch(reloadPage),
);
const VendorManagementSettings = lazy(() =>
  import("./pages/settings/VendorManagement/VendorManagementSettings").catch(
    reloadPage,
  ),
);
const VendorManagementSettingsCustomFields = lazy(() =>
  import(
    "./pages/settings/VendorManagement/VendorManagementSettingsCustomFields"
  ).catch(reloadPage),
);
const VendorManagementSettingsCustomColumns = lazy(() =>
  import(
    "./pages/settings/VendorManagement/VendorManagementSettingsCustomColumns"
  ).catch(reloadPage),
);
const IntakeSettings = lazy(() =>
  import("./pages/settings/intake-requests/IntakeSettings").catch(reloadPage),
);
const IntakeRequests = lazy(() =>
  import("./pages/intake-requests/IntakeRequests").catch(reloadPage),
);
const IntakeRequestDetails = lazy(() =>
  import("./pages/intake-requests/IntakeRequestDetails").catch(reloadPage),
);
const IntakeRequestSelection = lazy(() =>
  import("./pages/intake-requests/new/IntakeRequestSelection").catch(
    reloadPage,
  ),
);
const NewIntakeRequest = lazy(() =>
  import("./pages/intake-requests/new/NewIntakeRequest").catch(reloadPage),
);
const EditIntakeRequest = lazy(() =>
  import("./pages/intake-requests/edit/EditIntakeRequest").catch(reloadPage),
);
const DuplicateIntakeRequest = lazy(() =>
  import("./pages/intake-requests/duplicate/DuplicateIntakeRequest").catch(
    reloadPage,
  ),
);
const PurchaseOrders = lazy(() =>
  import("./pages/purchase-orders/PurchaseOrders").catch(reloadPage),
);
const Reports = lazy(() => import("./pages/reports/Reports").catch(reloadPage));
const RequestAnalytics = lazy(() =>
  import("./pages/reports/request-analytics/RequestAnalytics").catch(
    reloadPage,
  ),
);
const PurchaseOrderDetails = lazy(() =>
  import("./pages/purchase-orders/PurchaseOrderDetails").catch(reloadPage),
);
const EditIntakeCategoryForm = lazy(() =>
  import(
    "./pages/settings/intake-requests/edit-intake-category/EditIntakeCategoryForm"
  ).catch(reloadPage),
);
const EditIntakeCategoryIntegrations = lazy(() =>
  import(
    "./pages/settings/intake-requests/edit-intake-category/EditIntakeCategoryIntegrations"
  ).catch(reloadPage),
);
const EditIntakeCategoryDashboard = lazy(() =>
  import(
    "./pages/settings/intake-requests/edit-intake-category/EditIntakeCategoryDashboard"
  ).catch(reloadPage),
);
const EditIntakeCategoryWorkflows = lazy(() =>
  import(
    "./pages/settings/intake-requests/edit-intake-category/EditIntakeCategoryWorkflows"
  ).catch(reloadPage),
);
const VendorPortalForms = lazy(() =>
  import("./pages/vendor-portal/vendor-portal-forms/VendorPortalForms").catch(
    reloadPage,
  ),
);
const VendorPortalFormEdit = lazy(() =>
  import(
    "./pages/vendor-portal/vendor-portal-forms/VendorPortalFormEdit"
  ).catch(reloadPage),
);
const VendorPortalFormDetails = lazy(() =>
  import(
    "./pages/vendor-portal/vendor-portal-forms/VendorPortalFormDetails"
  ).catch(reloadPage),
);
const VendorPortalCompanySelection = lazy(() =>
  import(
    "./pages/vendor-portal/CompanySelection/VendorPortalCompanySelection"
  ).catch(reloadPage),
);
const PublicSourcingForm = lazy(() =>
  import("./pages/public/sourcing/SourcingForm").catch(reloadPage),
);
const SourcingForms = lazy(() =>
  import("./pages/sourcing/forms/SourcingForms").catch(reloadPage),
);
const SpendReviewReport = lazy(() =>
  import("./pages/spend-review-report/SpendReviewReport").catch(reloadPage),
);

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AuthorizedApolloProvider>
        <QueryClientProvider client={queryClient}>
          <SessionProvider>
            <MaybeSwitchCompany>
              <ErrorBoundary>
                <Suspense fallback={<PageLoading />}>
                  <GoogleMapsProvider>
                    <ImpersonationBorder>
                      <Router>
                        <QueryParamProvider
                          adapter={ReactRouter6Adapter}
                          options={{
                            searchStringToObject: parse,
                            objectToSearchString: stringify,
                            enableBatching: true,
                          }}
                        >
                          <NavProvider>
                            <Routes>
                              <Route
                                path={Page.HOME}
                                element={
                                  <PrivateRoute>
                                    <Home />
                                  </PrivateRoute>
                                }
                              />
                              <Route path={Page.LOGIN} element={<Login />} />
                              <Route
                                path={Page.LOGIN_WITH_TOKEN}
                                element={<LoginWithToken />}
                              />
                              <Route
                                path={Page.PUBLIC_SOURCING_FORM}
                                element={<PublicSourcingForm />}
                              />
                              <Route
                                path={Page.SPEND_REVIEW_REPORT}
                                element={
                                  <PrivateRoute>
                                    <SpendReviewReport />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.EDIT_COMPANY}
                                element={
                                  <PrivateRoute>
                                    <EditCompany />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.HEADCOUNT_IMPORT}
                                element={
                                  <PrivateRoute>
                                    <HeadcountImport />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.HEADCOUNT_IMPORT_MAP_DEPARTMENTS}
                                element={
                                  <PrivateRoute>
                                    <MapPlanDepartments hasHeadcount={true} />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.HEADCOUNT_IMPORT_MAP_LOCATIONS}
                                element={
                                  <PrivateRoute>
                                    <MapPlanLocations hasHeadcount={true} />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.HEADCOUNT_ROSTER}
                                element={
                                  <PrivateRoute>
                                    <HeadcountGatekeeper>
                                      <HeadcountRoster />
                                    </HeadcountGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.HEADCOUNT_INSIGHTS}
                                element={
                                  <PrivateRoute>
                                    <HeadcountGatekeeper>
                                      <HeadcountInsights />
                                    </HeadcountGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_HEADCOUNT_DEPARTMENTS}
                                element={
                                  <PrivateRoute>
                                    <ManageHrDepartments />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_HEADCOUNT_LOCATIONS}
                                element={
                                  <PrivateRoute>
                                    <ManageHrLocations />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.VENDORS}
                                element={
                                  <PrivateRoute>
                                    <Vendors />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_FORMS}
                                element={
                                  <PrivateRoute>
                                    <VendorForms />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_FORM_DETAILS}
                                element={
                                  <PrivateRoute>
                                    <IntakeRequestDetails type="VENDOR" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_FORM_DETAILS_TAB}
                                element={
                                  <PrivateRoute>
                                    <IntakeRequestDetails type="VENDOR" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_FORM_EDIT}
                                element={
                                  <PrivateRoute>
                                    <VendorPortalFormEdit />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.ADD_VENDOR}
                                element={
                                  <PrivateRoute>
                                    <AddNewVendor />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_DETAILS}
                                element={
                                  <PrivateRoute>
                                    <VendorDetails />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_DETAILS_TAB}
                                element={
                                  <PrivateRoute>
                                    <VendorDetails />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.EDIT_VENDOR}
                                element={
                                  <PrivateRoute>
                                    <UpdateVendor />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SERVICES}
                                element={<Services />}
                              />
                              <Route
                                path={Page.ADD_SERVICE}
                                element={
                                  <PrivateRoute>
                                    <AddNewService />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SERVICE_DETAILS}
                                element={
                                  <PrivateRoute>
                                    <ServiceDetails />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.EDIT_SERVICE}
                                element={
                                  <PrivateRoute>
                                    <UpdateService />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MEMBERS}
                                element={<Members />}
                              />
                              <Route
                                path={Page.EMAIL_FORWARDING}
                                element={
                                  <PrivateRoute>
                                    <EmailForwarding />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MEMBER_DETAILS}
                                element={
                                  <PrivateRoute>
                                    <MemberDetails />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.EDIT_MEMBER}
                                element={
                                  <PrivateRoute>
                                    <UpdateMember />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SPEND_REQUESTS}
                                element={
                                  <PrivateRoute>
                                    <SpendRequests />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.ADD_SPEND_REQUEST}
                                element={
                                  <PrivateRoute>
                                    <AddNewSpendRequest />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.DUPLICATE_SPEND_REQUEST}
                                element={
                                  <PrivateRoute>
                                    <DuplicateSpendRequest />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SPEND_REQUEST_DETAILS}
                                element={
                                  <PrivateRoute>
                                    <SpendRequestDetails />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.EDIT_SPEND_REQUEST}
                                element={
                                  <PrivateRoute>
                                    <UpdateSpendRequest />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.APPROVALS}
                                element={
                                  <PrivateRoute>
                                    <SpendRuleManager />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SPEND_RULES}
                                element={
                                  <PrivateRoute>
                                    <SpendRuleManager />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SPEND_RULES_SIMULATE}
                                element={
                                  <PrivateRoute>
                                    <SpendRulesSimulate />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SPEND_RULE}
                                element={
                                  <PrivateRoute>
                                    <SpendRuleManager activeTab="logic" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SPEND_RULE_NEW_CUSTOM_FIELDS}
                                element={
                                  <PrivateRoute>
                                    <SpendRuleManager activeTab="customFields" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_PAYMENTS_BILL_RULE}
                                element={
                                  <PrivateRoute>
                                    <BillRuleManager />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.BILL_RULES_SIMULATE}
                                element={
                                  <PrivateRoute>
                                    <BillRulesSimulate />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_PAYMENTS_BILL_RULES}
                                element={
                                  <PrivateRoute>
                                    <BillRuleManager />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_PAYMENT_DETAILS_APPROVERS}
                                element={
                                  <PrivateRoute>
                                    <ManagePaymentsSettingsPaymentInfoApprovers />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SPEND_CATEGORIES}
                                element={
                                  <PrivateRoute>
                                    <SpendCategories />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.REVENUE_CATEGORIES}
                                element={
                                  <PrivateRoute>
                                    <RevenueCategories />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.DOCUMENTS}
                                element={
                                  <PrivateRoute>
                                    <Documents />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.ADD_DOCUMENTS}
                                element={
                                  <PrivateRoute>
                                    <AddNewDocuments />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.DOCUMENT_DETAILS}
                                element={
                                  <PrivateRoute>
                                    <DocumentDetails />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.EDIT_DOCUMENT}
                                element={
                                  <PrivateRoute>
                                    <UpdateDocument />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.LOCATIONS}
                                element={
                                  <PrivateRoute>
                                    <Locations />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_GROUPS}
                                element={
                                  <PrivateRoute>
                                    <ManageGroups />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_APPROVER_MATRIX}
                                element={
                                  <PrivateRoute>
                                    <ManageApproverMatrix />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_APPROVER_MATRIX_EDIT}
                                element={
                                  <PrivateRoute>
                                    <ManageApproverMatrix />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_GROUP}
                                element={
                                  <PrivateRoute>
                                    <ManageGroups />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_DEPARTMENTS}
                                element={
                                  <PrivateRoute>
                                    <ManageDepartments />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_ENTITIES}
                                element={
                                  <PrivateRoute>
                                    <ManageEntities />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_BUSINESS_UNITS}
                                element={
                                  <PrivateRoute>
                                    <ManageBusinessUnits />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_BUSINESS_UNIT}
                                element={
                                  <PrivateRoute>
                                    <ManageBusinessUnits />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_DEPARTMENT}
                                element={
                                  <PrivateRoute>
                                    <ManageDepartments />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PLAN_IMPORT}
                                element={
                                  <PrivateRoute>
                                    <PlanUpload />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PLAN_IMPORT_MAP_DEPARTMENTS}
                                element={
                                  <PrivateRoute>
                                    <MapPlanDepartments hasHeadcount={false} />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PLAN_IMPORT_MAP_SPEND_CATEGORIES}
                                element={
                                  <PrivateRoute>
                                    <MapPlanSpendCategories />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PLAN_IMPORT_MAP_REVENUE_CATEGORIES}
                                element={
                                  <PrivateRoute>
                                    <MapPlanRevenueCategories />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PLAN_IMPORT_MAP_LOCATIONS}
                                element={
                                  <PrivateRoute>
                                    <MapPlanLocations hasHeadcount={false} />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PLAN_IMPORT_MAP_VENDORS}
                                element={
                                  <PrivateRoute>
                                    <MapPlanVendors />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PLAN_IMPORT_MAP_BUSINESS_UNITS}
                                element={
                                  <PrivateRoute>
                                    <MapPlanBusinessUnits />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PLANS}
                                element={
                                  <PrivateRoute>
                                    <Plans />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PLAN}
                                element={
                                  <PrivateRoute>
                                    <PlansDashboard />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.EDIT_PLAN}
                                element={
                                  <PrivateRoute>
                                    <EditPlan />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SUPER_ADMIN}
                                element={
                                  <SuperPrivateRoute>
                                    <SuperAdmin />
                                  </SuperPrivateRoute>
                                }
                              />
                              <Route
                                path={Page.ACTUALS}
                                element={
                                  <PrivateRoute>
                                    <Actuals />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.ACTUALS_WIZARD}
                                element={
                                  <PrivateRoute>
                                    <Actuals />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.ACTUALS_LOGIN}
                                element={
                                  <PrivateRoute>
                                    <Actuals />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.ACTUALS_SYNC_STATUS}
                                element={
                                  <PrivateRoute>
                                    <Actuals />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.TRANSACTIONS}
                                element={
                                  <PrivateRoute>
                                    <Transactions />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.TRANSACTIONS_COMMENTS}
                                element={
                                  <PrivateRoute>
                                    <Transactions />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.FINANCIAL_INSIGHTS}
                                element={
                                  <PrivateRoute>
                                    <FinancialInsights />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.USER_NOTIFICATION_SETTINGS}
                                element={
                                  <PrivateRoute>
                                    <UserNotificationSettings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.USER_DELEGATE_APPROVER_SETTINGS}
                                element={
                                  <PrivateRoute>
                                    <DelegateApproverSettings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_ACTUALS_MAPPINGS}
                                element={
                                  <PrivateRoute>
                                    <ManageActualsMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.MANAGE_ACTUALS_MAPPINGS_ERP_EXPENSE_ACCOUNTS
                                }
                                element={
                                  <PrivateRoute>
                                    <ManageActualsMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_ACTUALS_MAPPINGS_ERP_ENTITIES}
                                element={
                                  <PrivateRoute>
                                    <ManageActualsMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.MANAGE_ACTUALS_MAPPINGS_ERP_REVENUE_ACCOUNTS
                                }
                                element={
                                  <PrivateRoute>
                                    <ManageActualsMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.MANAGE_ACTUALS_MAPPINGS_ERP_DEPARTMENTS
                                }
                                element={
                                  <PrivateRoute>
                                    <ManageActualsMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_ACTUALS_MAPPINGS_ERP_VENDORS}
                                element={
                                  <PrivateRoute>
                                    <ManageActualsMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.MANAGE_ACTUALS_MAPPINGS_ERP_LOCATIONS
                                }
                                element={
                                  <PrivateRoute>
                                    <ManageActualsMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.MANAGE_ACTUALS_MAPPINGS_ERP_CLASSIFICATIONS
                                }
                                element={
                                  <PrivateRoute>
                                    <ManageActualsMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_PLANS_MAPPINGS}
                                element={
                                  <PrivateRoute>
                                    <ManageDepartmentMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_PLANS_MAPPINGS_DEPARTMENTS}
                                element={
                                  <PrivateRoute>
                                    <ManageDepartmentMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.MANAGE_PLANS_MAPPINGS_SPEND_CATEGORIES
                                }
                                element={
                                  <PrivateRoute>
                                    <ManagePlanExpenseCategoryMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.MANAGE_PLANS_MAPPINGS_REVENUE_CATEGORIES
                                }
                                element={
                                  <PrivateRoute>
                                    <ManagePlanRevenueCategoryMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_PLANS_MAPPINGS_LOCATIONS}
                                element={
                                  <PrivateRoute>
                                    <ManageLocationMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_PLANS_MAPPINGS_VENDORS}
                                element={
                                  <PrivateRoute>
                                    <ManageVendorMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_PLANS_MAPPINGS_BUSINESS_UNITS}
                                element={
                                  <PrivateRoute>
                                    <ManagePlanBusinessUnitMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.BILLS}
                                element={
                                  <PrivateRoute>
                                    <PaymentsGatekeeper>
                                      <Bills />
                                    </PaymentsGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.NEW_BILL}
                                element={
                                  <PrivateRoute>
                                    <PaymentsGatekeeper>
                                      <NewBill />
                                    </PaymentsGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.NEW_BILL_FROM_INVOICE}
                                element={
                                  <PrivateRoute>
                                    <PaymentsGatekeeper>
                                      <NewBill />
                                    </PaymentsGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.BILL_DETAILS}
                                element={
                                  <PrivateRoute>
                                    <PaymentsGatekeeper>
                                      <BillDetails />
                                    </PaymentsGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.BILL_DETAILS_TAB}
                                element={
                                  <PrivateRoute>
                                    <PaymentsGatekeeper>
                                      <BillDetails />
                                    </PaymentsGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.EDIT_BILL}
                                element={
                                  <PrivateRoute>
                                    <PaymentsGatekeeper>
                                      <EditBill />
                                    </PaymentsGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PAYMENTS_TRANSACTIONS}
                                element={
                                  <PrivateRoute>
                                    <PaymentsGatekeeper>
                                      <PaymentsTransactions />
                                    </PaymentsGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_INTEGRATIONS}
                                element={
                                  <PrivateRoute>
                                    <Integrations />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_INTEGRATIONS_MAPPINGS}
                                element={
                                  <PrivateRoute>
                                    <IntegrationsMappings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.NOTIFICATION_SETTINGS}
                                element={
                                  <PrivateRoute>
                                    <NotificationSettings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.NOTIFICATION_SETTINGS_REMINDERS}
                                element={
                                  <PrivateRoute>
                                    <NotiticationSettingsReminders />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.NOTIFICATION_SETTINGS_VENDORS}
                                element={
                                  <PrivateRoute>
                                    <NotiticationSettingsVendors />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_MANAGEMENT_SETTINGS}
                                element={
                                  <PrivateRoute>
                                    <VendorManagementSettings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.VENDOR_MANAGEMENT_SETTINGS_CUSTOM_FIELDS
                                }
                                element={
                                  <PrivateRoute>
                                    <VendorManagementSettingsCustomFields />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.VENDOR_MANAGEMENT_SETTINGS_CUSTOM_COLUMNS
                                }
                                element={
                                  <PrivateRoute>
                                    <VendorManagementSettingsCustomColumns />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_API_KEYS}
                                element={
                                  <PrivateRoute>
                                    <ManageApiKeys />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SOURCING_SETTINGS}
                                element={
                                  <PrivateRoute>
                                    <SourcingSettings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SOURCING_SETTINGS_CATEGORY_EDIT_FORM}
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryForm type="PUBLIC" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SOURCING_SETTINGS_EDIT_WORKFLOW}
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryWorkflows type="PUBLIC" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SOURCING_SETTINGS_EDIT_WORKFLOWS}
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryWorkflows type="PUBLIC" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SOURCING_SETTINGS_EDIT_ADVANCED}
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryDashboard type="PUBLIC" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.VENDOR_INTAKE_REQUEST_SETTINGS_ADVANCED
                                }
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryDashboard type="VENDOR" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.INTAKE_REQUEST_SETTINGS}
                                element={
                                  <PrivateRoute>
                                    <IntakeSettings />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.INTAKE_REQUEST_SETTINGS_CATEGORY_EDIT_ADVANCED
                                }
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryDashboard type="INTERNAL" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.INTAKE_REQUEST_SETTINGS_CATEGORY_EDIT_FORM
                                }
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryForm type="INTERNAL" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.INTAKE_REQUEST_SETTINGS_INTEGRATIONS_EDIT
                                }
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryIntegrations />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.VENDOR_INTAKE_REQUEST_SETTINGS_CATEGORY_EDIT_FORM
                                }
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryForm type="VENDOR" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.INTAKE_REQUEST_SETTINGS_CATEGORY_EDIT_WORKFLOWS
                                }
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryWorkflows type="INTERNAL" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.VENDOR_INTAKE_REQUEST_SETTINGS_CATEGORY_EDIT_WORKFLOWS
                                }
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryWorkflows type="VENDOR" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.INTAKE_REQUEST_SETTINGS_CATEGORY_EDIT_WORKFLOW
                                }
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryWorkflows type="INTERNAL" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={
                                  Page.VENDOR_INTAKE_REQUEST_SETTINGS_CATEGORY_EDIT_WORKFLOW
                                }
                                element={
                                  <PrivateRoute>
                                    <EditIntakeCategoryWorkflows type="VENDOR" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.INTAKE_REQUESTS}
                                element={
                                  <PrivateRoute>
                                    <IntakeRequests />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PURCHASE_ORDER_DETAILS}
                                element={
                                  <PrivateRoute>
                                    <PurchaseOrderDetails />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PURCHASE_ORDERS}
                                element={
                                  <PrivateRoute>
                                    <PurchaseOrders />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.REPORTS}
                                element={
                                  <PrivateRoute>
                                    <Reports />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.REQUEST_ANALYTICS}
                                element={
                                  <PrivateRoute>
                                    <RequestAnalytics />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.INTAKE_REQUESTS_FOR_CATEGORY}
                                element={
                                  <PrivateRoute>
                                    <IntakeRequests />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.INTAKE_REQUEST_DETAILS}
                                element={
                                  <PrivateRoute>
                                    <IntakeRequestDetails type="INTERNAL" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.INTAKE_REQUEST_DETAILS_TAB}
                                element={
                                  <PrivateRoute>
                                    <IntakeRequestDetails type="INTERNAL" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.NEW_INTAKE_REQUEST_SELECTION}
                                element={
                                  <PrivateRoute>
                                    <IntakeRequestSelection />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.NEW_INTAKE_REQUEST}
                                element={
                                  <PrivateRoute>
                                    <NewIntakeRequest />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.EDIT_INTAKE_REQUEST}
                                element={
                                  <PrivateRoute>
                                    <EditIntakeRequest />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.DUPLICATE_INTAKE_REQUEST}
                                element={
                                  <PrivateRoute>
                                    <DuplicateIntakeRequest />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PAYMENTS}
                                element={
                                  <PrivateRoute>
                                    <BillPayments />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.PAYMENTS_INVOICES}
                                element={
                                  <PrivateRoute>
                                    <PaymentsGatekeeper>
                                      <Invoices />
                                    </PaymentsGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.BILL_PAYMENT_DETAILS}
                                element={
                                  <PrivateRoute>
                                    <BillPaymentDetails />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_PAYMENTS_ONBOARDING}
                                element={
                                  <PrivateRoute>
                                    <ManagePaymentsOnboarding />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.MANAGE_PAYMENTS_SETTINGS}
                                element={
                                  <PrivateRoute>
                                    <PaymentsGatekeeper>
                                      <ManagePaymentsSettings />
                                    </PaymentsGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SYNCED_LEDGER_ENTRIES}
                                element={
                                  <PrivateRoute>
                                    <PaymentsGatekeeper>
                                      <SyncedLedgerEntries />
                                    </PaymentsGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.UNSYNCED_LEDGER_ENTRIES}
                                element={
                                  <PrivateRoute>
                                    <PaymentsGatekeeper>
                                      <SyncedLedgerEntries />
                                    </PaymentsGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.ERRORED_LEDGER_ENTRIES}
                                element={
                                  <PrivateRoute>
                                    <PaymentsGatekeeper>
                                      <LedgerEntries />
                                    </PaymentsGatekeeper>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SOURCING_FORMS}
                                element={
                                  <PrivateRoute>
                                    <SourcingForms />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SOURCING_FORM_DETAILS}
                                element={
                                  <PrivateRoute>
                                    <IntakeRequestDetails type="PUBLIC" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.SOURCING_FORM_DETAILS_TAB}
                                element={
                                  <PrivateRoute>
                                    <IntakeRequestDetails type="PUBLIC" />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_PORTAL_LOGIN}
                                element={<VendorLogin />}
                              />
                              <Route
                                path={Page.VENDOR_PORTAL_HOME}
                                element={
                                  <PrivateVendorRoute>
                                    <VendorPortalHome />
                                  </PrivateVendorRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_PORTAL_PAYMENTS}
                                element={
                                  <PrivateVendorRoute>
                                    <VendorPortalPayments />
                                  </PrivateVendorRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_PORTAL_COMPANY_PROFILE}
                                element={
                                  <PrivateVendorRoute>
                                    <VendorPortalCompanyProfile />
                                  </PrivateVendorRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_PORTAL_MEMBERS}
                                element={
                                  <PrivateVendorRoute>
                                    <VendorPortalMembers />
                                  </PrivateVendorRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_PORTAL_CUSTOMERS}
                                element={
                                  <PrivateVendorRoute>
                                    <VendorPortalCustomers />
                                  </PrivateVendorRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_PORTAL_FORMS}
                                element={
                                  <PrivateVendorRoute>
                                    <VendorPortalForms />
                                  </PrivateVendorRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_PORTAL_FORM_EDIT}
                                element={
                                  <PrivateVendorRoute>
                                    <VendorPortalFormEdit />
                                  </PrivateVendorRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_PORTAL_FORM_DETAILS}
                                element={
                                  <PrivateVendorRoute>
                                    <VendorPortalFormDetails />
                                  </PrivateVendorRoute>
                                }
                              />
                              <Route
                                path={Page.VENDOR_PORTAL_COMPANY_SELECTION}
                                element={
                                  <PrivateVendorRoute>
                                    <VendorPortalCompanySelection />
                                  </PrivateVendorRoute>
                                }
                              />
                              <Route
                                path={Page.NOT_FOUND}
                                element={<NotFound />}
                              />
                              <Route path="*" element={<NotFound />} />
                            </Routes>
                          </NavProvider>
                        </QueryParamProvider>
                      </Router>
                    </ImpersonationBorder>
                  </GoogleMapsProvider>
                </Suspense>
              </ErrorBoundary>
            </MaybeSwitchCompany>
          </SessionProvider>
        </QueryClientProvider>
      </AuthorizedApolloProvider>
      <Toaster containerStyle={{ zIndex: 1000000 }} />
    </AuthProvider>
  );
};

export default App;
